import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, FormControlLabel, Grid2 as Grid, TextField, Tooltip} from '@mui/material';
import {Add, ArrowDropUp, FilterAltOff, KeyboardArrowDown} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import productService from '../../services/ProductService';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import FilterPanel from "../common/FilterPanel";
import IconButton from "@mui/material/IconButton";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import {limit} from "../../util/StringUtils";

const ALL_COLUMNS = [
    {id: 'name', label: 'Termék', width: "auto", fun: d => <Tooltip title={d} placement="left">{limit(d, 50)}</Tooltip>},
    {id: 'manufacturer', label: 'Gyártó'},
    {id: 'centralStorageLocation', label: 'Közpönti Raktár Hely'},
    {id: 'ean', label: 'EAN'},
    {id: 'articleNumber', label: 'Cikkszám'},
    {id: 'vtszSzjTeszor', label: 'Vtsz/Szj/TESZOR'},
    {id: 'component', label: 'Komponensek'},
    {id: 'stockManagement', label: 'Készletkezelés'},
    {id: 'dataSheetUrl', label: 'Adatlap URL'},
    {id: 'scoreSheetUrl', label: 'Teljesítménylap URL'},
    {id: 'comment', label: 'Megjegyzés'},
    {id: 'unitOfMeasurement', label: 'Mértékegység'},
    {id: 'createdAt', label: 'Létrehozva', head: <></>},
    {id: 'updatedAt', label: 'Módosítva', head: <></>},
    {id: 'totalAmount', label: 'Teljes Mennyiség', head: <></>}
]

const DEFAULT_COLUMNS = ['name', 'manufacturer', 'ean', 'articleNumber', 'totalAmount', 'updatedAt'];

const LOCAL_STORAGE_ITEM_NAME = "product-rows-per-page"
const LOCAL_STORAGE_SEARCH_PARAMS = "product-search-params"
const LOCAL_STORAGE_COLUMN_NAMES = "product-column-names"
const DEFAULT_SEARCH_PARAMS = {}

export default function ProductTable() {

    useTitle([{name: 'Raktár'}])

    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })
    const [expanded, setExpanded] = useState(true);
    const [searchParams, setSearchParams] = useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)))
    const {sendAlertMessage} = useSnackbar()
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const filterPanelRef = React.useRef();

    useEffect(() => {
        productService.searchProducts(buildQueryParams())
            .then(response => setTableData((prev) => ({...prev, totalCount: response.totalElements, rows: response.content})))
            .catch((error) => {
                sendAlertMessage(error?.message || 'Termékek lekérése sikertelen!', 'error')
                setTableData((prev) => ({...prev, rows: []}))
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.page, tableData.rowsPerPage, searchParams]);

    function buildQueryParams() {
        const filteredSearchParams = searchParams ? Object.fromEntries(Object.entries(searchParams).filter(([, value]) => value !== '')) : {};
        return {
            page: tableData.page,
            size: tableData.rowsPerPage,
            ...filteredSearchParams
        }
    }

    const onSearchParamsChange = (event) => {
        const {name, value} = event.target
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const clear = () => {
        if (searchParams != null) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    useEffect(() => {
        if (columnNames) localStorage.setItem(LOCAL_STORAGE_COLUMN_NAMES, JSON.stringify(columnNames));
        else localStorage.removeItem(LOCAL_STORAGE_COLUMN_NAMES)
    }, [columnNames]);

    const onChangeColumnCheckBox = (event) => {
        const {checked, name} = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };

    const navigate = useNavigate();

    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Keresési Feltételek
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>

                                <Grid size={{xs: 20}}>
                                    {ALL_COLUMNS.map((item, index) => (
                                        <FormControlLabel key={index}
                                                          control={<Checkbox
                                                              variant="outlined"
                                                              margin="normal"
                                                              size="large"
                                                              name={item.id}
                                                              onChange={onChangeColumnCheckBox}
                                                              checked={columnNames?.includes(item.id) || false}
                                                              sx={{padding: "0px"}}
                                                          />} label={item.label?.props?.title || item.label}
                                                          style={{paddingRight: "20px", margin: "0px"}}/>
                                    ))}
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="PRODUCT"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Paper sx={{width: '100%', overflow: 'hidden'}}>
                <TableContainer style={{maxHeight: "calc(100vh - 360px)"}}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "120px", paddingLeft: "10px"}}
                                           align='left' padding='none'>
                                    <Tooltip title="Új hozzáadása">
                                        <Button variant='contained' color='success'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={() => navigate("/dashboard/tasks/create")}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {columnNames?.map(columnName => ALL_COLUMNS.find(column => column.id === columnName)).map((column) => (
                                    <TableCell key={column.id} align={column.align} sx={{width: column.head ? "200px" : ""}}>
                                        {column.head ? <span>{column.label}</span> : <TextField name={column.id} label={column.label} size="small"
                                                                                                value={searchParams?.[column.id] || ""} onChange={onSearchParamsChange}
                                                                                                sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>}
                                        <IconButton onClick={() => setSearchParams(prev => ({
                                            ...prev,
                                            orderBy: column.id,
                                            sort: searchParams?.sort === 'ASC' ? 'DESC' : 'ASC'
                                        }))}>
                                            {searchParams?.sort == null || searchParams?.orderBy !== column.id ?
                                                <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                                searchParams?.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                        </IconButton>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows == null ? <TableRow sx={{textAlign: "center"}}><TableCell colSpan={17} sx={{
                                    padding: "200px",
                                    textAlign: "center"
                                }}><CircularProgress/></TableCell></TableRow> :
                                tableData.rows.map((row) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                            <TableCell></TableCell>
                                            {columnNames?.map(columnName => ALL_COLUMNS.find(column => column.id === columnName)).map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell key={column.id} align={column.align}
                                                               onClick={() => navigate("/dashboard/storage/products/" + row.id)}
                                                               sx={{
                                                                   cursor: "pointer", padding: "10px", width: "auto" || "",
                                                                   whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"
                                                               }}>
                                                        {column.fun ? column.fun(value) : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[3, 10, 25, 50]}
                    component="div"
                    count={tableData.totalCount}
                    rowsPerPage={parseInt(tableData.rowsPerPage)}
                    page={tableData.page}
                    onPageChange={(event, newPage) => setTableData((prev) => ({...prev, page: newPage}))}
                    onRowsPerPageChange={(event) => {
                        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
                        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
                    }}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>
        </>
    );
}
